import type {
  BrokerCapabilities,
  BrokerCsvImportCapabilities,
} from "../services/brokerIntegrationApi";
import { parseIbkrFlexPortalSetup, type IbkrFlexPortalSetupInfo } from "./ibkrBrokerSetup";
import { CTRADER_DEFAULT_OPENAPI_LOOKBACK_DAYS } from "./ctraderOpenApiSync";

export type BrokerHistoryPath = "recent_only" | "needs_csv_for_history";

export interface BrokerTradeSyncStep {
  action: string;
  label: string;
  covers?: string;
  covers_from_date?: string;
  covers_to_date?: string;
  endpoint?: string;
  requiredFields?: string[];
  dateFormatOptions?: string[];
  timeFormatOptions?: string[];
}

export interface BrokerCsvImportCaps {
  supported: boolean;
  endpoint?: string;
  requiredFields: string[];
  optionalFields: string[];
  dateFormatOptions: string[];
  timeFormatOptions: string[];
  fieldLabels: Record<string, string>;
  allowedAssetClasses: string[];
  fixedAssetClasses: string[];
  maxLookbackDaysPerFile: number | null;
  requiresTradeTimestampTimezone: boolean;
  notes: string;
}

export interface BrokerTradeSyncInfo {
  strategy: string;
  requiredCredentialFields: string[];
  credentialFieldLabels: Record<string, string>;
  summary: string;
  steps: BrokerTradeSyncStep[];
  csvImport: BrokerCsvImportCaps | null;
  accountCodeInferredFromReport: boolean;
  statementTimeZoneInferredFromReport: boolean;
  requiresTradeTimestampTimezone: boolean;
  portalSetup: IbkrFlexPortalSetupInfo | null;
  csvImportEndpoint?: string;
  syncEndpoint?: string;
  /** Flex/API automated sync window (from capabilities). */
  syncLookbackDays: number;
  syncFromDate: string;
  syncToDate: string;
  historyBeforeSyncRequiresCsvImport: boolean;
  csvMaxLookbackDaysPerFile: number | null;
  csvImportNotes: string;
  tradeSyncNotes: string;
  fullHistoryRecommendation: string | null;
}

const STEP_DEFAULT_LABELS: Record<string, string> = {
  credentials: "Connect — save Flex credentials",
  api_sync: "Sync — pull trades from your Flex statement",
  csv_import: "Import Flex statement CSV",
};

/** Drop legacy or overly technical API summaries from the UI. */
function isVerboseIbkrApiSummary(summary: string): boolean {
  return /SendRequest|GetStatement|Flex Web Service|non-overlapping|on-demand broker/i.test(
    summary
  );
}

function formatYmd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function positiveInt(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value) && value > 0) {
    return Math.floor(value);
  }
  if (typeof value === "string" && value.trim()) {
    const n = Number(value);
    if (Number.isFinite(n) && n > 0) return Math.floor(n);
  }
  return null;
}

function defaultLookbackDates(lookbackDays: number): { from: string; to: string } {
  const to = new Date();
  const from = new Date(to);
  from.setDate(from.getDate() - lookbackDays);
  return { from: formatYmd(from), to: formatYmd(to) };
}

export function formatBrokerSyncDateRange(from: string, to: string): string {
  try {
    const f = new Date(`${from}T12:00:00`);
    const t = new Date(`${to}T12:00:00`);
    if (Number.isNaN(f.getTime()) || Number.isNaN(t.getTime())) return `${from} – ${to}`;
    const opts: Intl.DateTimeFormatOptions = { month: "short", day: "numeric", year: "numeric" };
    return `${f.toLocaleDateString(undefined, opts)} – ${t.toLocaleDateString(undefined, opts)}`;
  } catch {
    return `${from} – ${to}`;
  }
}

function parseNotes(raw: unknown): string {
  if (typeof raw === "string" && raw.trim()) return raw.trim();
  if (Array.isArray(raw)) {
    return raw.map((v) => String(v).trim()).filter(Boolean).join(" ");
  }
  return "";
}

function parseStringRecord(raw: unknown): Record<string, string> {
  if (!raw || typeof raw !== "object") return {};
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof value === "string" && value.trim()) {
      out[key] = value.trim();
    }
  }
  return out;
}

function parseStringArray(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((v) => String(v).trim()).filter(Boolean);
}

function mergeImportCaps(
  primary: BrokerCsvImportCaps,
  secondary: BrokerCsvImportCaps
): BrokerCsvImportCaps {
  return {
    supported: true,
    endpoint: primary.endpoint || secondary.endpoint,
    requiredFields:
      primary.requiredFields.length > 0 ? primary.requiredFields : secondary.requiredFields,
    optionalFields:
      primary.optionalFields.length > 0 ? primary.optionalFields : secondary.optionalFields,
    dateFormatOptions:
      primary.dateFormatOptions.length > 0
        ? primary.dateFormatOptions
        : secondary.dateFormatOptions,
    timeFormatOptions:
      primary.timeFormatOptions.length > 0
        ? primary.timeFormatOptions
        : secondary.timeFormatOptions,
    fieldLabels: { ...secondary.fieldLabels, ...primary.fieldLabels },
    allowedAssetClasses:
      primary.allowedAssetClasses.length > 0
        ? primary.allowedAssetClasses
        : secondary.allowedAssetClasses,
    fixedAssetClasses:
      primary.fixedAssetClasses.length > 0
        ? primary.fixedAssetClasses
        : secondary.fixedAssetClasses,
    maxLookbackDaysPerFile:
      primary.maxLookbackDaysPerFile ?? secondary.maxLookbackDaysPerFile,
    requiresTradeTimestampTimezone:
      primary.requiresTradeTimestampTimezone || secondary.requiresTradeTimestampTimezone,
    notes: primary.notes || secondary.notes,
  };
}

function mergeNullableImportCaps(
  a: BrokerCsvImportCaps | null,
  b: BrokerCsvImportCaps | null
): BrokerCsvImportCaps | null {
  if (!a) return b;
  if (!b) return a;
  return mergeImportCaps(a, b);
}

function normalizeCsvImportBlock(
  raw: BrokerCsvImportCapabilities | null | undefined
): BrokerCsvImportCaps | null {
  if (!raw || !raw.supported) return null;
  const maxDays =
    typeof raw.max_lookback_days_per_file === "number" && raw.max_lookback_days_per_file > 0
      ? raw.max_lookback_days_per_file
      : null;
  return {
    supported: true,
    endpoint: raw.endpoint != null ? String(raw.endpoint) : undefined,
    requiredFields: parseStringArray(raw.required_fields),
    optionalFields: parseStringArray(raw.optional_fields),
    dateFormatOptions: parseStringArray(raw.date_format_options),
    timeFormatOptions: parseStringArray(raw.time_format_options),
    fieldLabels: parseStringRecord(raw.field_labels),
    allowedAssetClasses: parseStringArray(raw.allowed_asset_classes),
    fixedAssetClasses: parseStringArray(raw.fixed_traded_asset_classes),
    maxLookbackDaysPerFile: maxDays,
    requiresTradeTimestampTimezone: Boolean(raw.requires_trade_timestamp_timezone),
    notes: parseNotes(raw.notes),
  };
}

/**
 * Statement import contract from capabilities — merges `csv_import`, `xlsx_import`, `html_import`,
 * and nested `trade_sync` variants (IBKR Flex CSV, MT5 Trade History .xlsx, MT4 HTML, TradingView CSV).
 */
export function resolveBrokerCsvImport(
  cap: BrokerCapabilities | null | undefined
): BrokerCsvImportCaps | null {
  if (!cap) return null;
  const tradeSync = cap.trade_sync as
    | {
        csv_import?: BrokerCsvImportCapabilities;
        xlsx_import?: BrokerCsvImportCapabilities;
        html_import?: BrokerCsvImportCapabilities;
      }
    | undefined;
  const fromTop = mergeNullableImportCaps(
    normalizeCsvImportBlock(cap.csv_import),
    mergeNullableImportCaps(
      normalizeCsvImportBlock(cap.xlsx_import),
      normalizeCsvImportBlock(cap.html_import)
    )
  );
  const fromTradeSync = mergeNullableImportCaps(
    normalizeCsvImportBlock(tradeSync?.csv_import),
    mergeNullableImportCaps(
      normalizeCsvImportBlock(tradeSync?.xlsx_import),
      normalizeCsvImportBlock(tradeSync?.html_import)
    )
  );
  if (fromTradeSync) {
    if (!fromTop) return fromTradeSync;
    return mergeImportCaps(fromTradeSync, fromTop);
  }
  return fromTop;
}

function defaultFlexStatementSteps(
  from: string,
  to: string,
  csvMaxDays: number
): BrokerTradeSyncStep[] {
  return [
    {
      action: "api_sync",
      label: "Sync recent via Flex",
      covers_from_date: from,
      covers_to_date: to,
    },
    {
      action: "csv_import",
      label: "Import older Flex CSV(s)",
      covers: `Trades before ${from} — up to ${csvMaxDays} days per file; use multiple exports for longer history`,
    },
  ];
}

function parseSteps(
  raw: unknown,
  strategy: string,
  from: string,
  to: string,
  csvMaxDays: number
): BrokerTradeSyncStep[] {
  const parsed = parseStepsRaw(raw);
  if (parsed.length > 0) return parsed;
  if (strategy === "flex_statement") {
    return defaultFlexStatementSteps(from, to, csvMaxDays);
  }
  return parsed;
}

function parseStepsRaw(raw: unknown): BrokerTradeSyncStep[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item) => {
      if (!item || typeof item !== "object") return null;
      const o = item as Record<string, unknown>;
      const action = o.action != null ? String(o.action).trim() : "";
      if (!action) return null;
      const defaultLabel = STEP_DEFAULT_LABELS[action] || action;
      return {
        action,
        label: String(o.label || defaultLabel).trim(),
        covers: o.covers != null ? String(o.covers) : undefined,
        covers_from_date:
          o.covers_from_date != null ? String(o.covers_from_date).slice(0, 10) : undefined,
        covers_to_date:
          o.covers_to_date != null ? String(o.covers_to_date).slice(0, 10) : undefined,
        endpoint: o.endpoint != null ? String(o.endpoint) : undefined,
        requiredFields: parseStringArray(o.required_fields),
        dateFormatOptions: parseStringArray(o.date_format_options),
        timeFormatOptions: parseStringArray(o.time_format_options),
      };
    })
    .filter(Boolean) as BrokerTradeSyncStep[];
}

/** Strip trailing “(e.g. …)” from API field labels — example belongs in placeholder only. */
export function stripExampleFromFieldLabel(label: string): string {
  return label.replace(/\s*\(e\.g\.\s[^)]+\)\s*/gi, " ").replace(/\s+/g, " ").trim();
}

function historyPathStorageKey(brokerSlug: string): string {
  return `tessera.${brokerSlug}_trade_history_path`;
}

export function loadBrokerHistoryPath(brokerSlug: string): BrokerHistoryPath | null {
  if (typeof window === "undefined") return null;
  const v = window.sessionStorage.getItem(historyPathStorageKey(brokerSlug));
  if (v === "recent_only" || v === "needs_csv_for_history") return v;
  return null;
}

export function saveBrokerHistoryPath(brokerSlug: string, path: BrokerHistoryPath): void {
  if (typeof window === "undefined") return;
  window.sessionStorage.setItem(historyPathStorageKey(brokerSlug), path);
}

export function clearBrokerHistoryPath(brokerSlug: string): void {
  if (typeof window === "undefined") return;
  window.sessionStorage.removeItem(historyPathStorageKey(brokerSlug));
}

/** Read `trade_sync` from GET /broker/capabilities (IBKR flex_statement, etc.). */
export function resolveBrokerTradeSync(
  cap: BrokerCapabilities | null | undefined
): BrokerTradeSyncInfo | null {
  if (!cap) return null;
  const raw = (cap as { trade_sync?: Record<string, unknown> }).trade_sync;
  if (!raw || typeof raw !== "object") return null;

  const strategy = String(raw.strategy || "");
  const brokerSlug = String(cap.broker_name || "").toLowerCase();

  const openapiRaw =
    raw.openapi_sync && typeof raw.openapi_sync === "object"
      ? (raw.openapi_sync as Record<string, unknown>)
      : null;

  const openapiLookback =
    positiveInt(openapiRaw?.max_lookback_days) ??
    positiveInt(openapiRaw?.api_sync_lookback_days) ??
    positiveInt(openapiRaw?.sync_lookback_days);

  const lookbackDays =
    positiveInt(raw.flex_query_max_lookback_days) ??
    positiveInt(raw.api_sync_lookback_days) ??
    positiveInt(raw.max_lookback_days) ??
    openapiLookback ??
    (strategy === "flex_statement" ? 365 : null) ??
    (brokerSlug === "ctrader" ? CTRADER_DEFAULT_OPENAPI_LOOKBACK_DAYS : 30);

  const fallbackDates = defaultLookbackDates(lookbackDays);
  const syncFromDate =
    raw.api_sync_from_date != null
      ? String(raw.api_sync_from_date).slice(0, 10)
      : fallbackDates.from;
  const syncToDate =
    raw.api_sync_to_date != null
      ? String(raw.api_sync_to_date).slice(0, 10)
      : fallbackDates.to;

  const fullHistoryRecommendationEarly =
    raw.full_history_recommendation != null
      ? String(raw.full_history_recommendation).trim() || null
      : null;

  const historyBeforeSyncRequiresCsvImport = Boolean(
    raw.history_before_flex_query_requires_csv_import ??
      raw.history_before_api_requires_csv_import ??
      fullHistoryRecommendationEarly === "csv_import"
  );

  const guidance =
    raw.user_guidance && typeof raw.user_guidance === "object"
      ? (raw.user_guidance as Record<string, unknown>)
      : {};

  const csvImport = resolveBrokerCsvImport(cap);
  const csvMaxLookbackDaysPerFile =
    csvImport?.maxLookbackDaysPerFile ??
    (strategy === "flex_statement" ? 365 : null);

  const csvImportNotes = csvImport?.notes || "";
  const tradeSyncNotes = parseNotes(raw.notes);

  const rawSummary =
    typeof guidance.summary === "string" && guidance.summary.trim()
      ? guidance.summary.trim()
      : "";

  const rangeLabel = formatBrokerSyncDateRange(syncFromDate, syncToDate);
  const perFileDays = csvMaxLookbackDaysPerFile ?? 365;

  const summary =
    rawSummary && !isVerboseIbkrApiSummary(rawSummary)
      ? rawSummary
      : strategy === "flex_statement"
        ? `Flex sync covers the last ${lookbackDays} days. Use CSV import for older history (up to ${perFileDays} days per file).`
        : rawSummary;

  const credentialLabels = {
    ...parseStringRecord(raw.credential_field_labels),
  };

  const csvEndpoint =
    csvImport?.endpoint ||
    (raw.csv_import_endpoint != null ? String(raw.csv_import_endpoint) : undefined);

  const openapiSyncEndpoint =
    openapiRaw?.supported !== false && openapiRaw?.sync_endpoint != null
      ? String(openapiRaw.sync_endpoint).trim()
      : "";

  const requiredCredentialFields = parseStringArray(raw.required_credential_fields);

  const accountCodeInferredFromReport = Boolean(
    raw.account_code_inferred_from_report ??
      (cap.auth as { account_code_inferred_from_report?: boolean })
        ?.account_code_inferred_from_report
  );

  const statementTimeZoneInferredFromReport = Boolean(
    raw.statement_time_zone_inferred_from_report
  );

  const requiresTradeTimestampTimezone = Boolean(
    raw.requires_trade_timestamp_timezone ??
      csvImport?.requiresTradeTimestampTimezone ??
      (strategy === "flex_statement" || brokerSlug === "ibkr")
  );

  const portalSetup =
    strategy === "flex_statement" || brokerSlug === "ibkr"
      ? parseIbkrFlexPortalSetup(raw.ibkr_flex_portal_setup)
      : null;

  const fullHistoryRecommendation =
    raw.full_history_recommendation != null
      ? String(raw.full_history_recommendation).trim() || null
      : null;

  return {
    strategy,
    requiredCredentialFields,
    credentialFieldLabels: credentialLabels,
    summary,
    steps: parseSteps(guidance.steps, strategy, syncFromDate, syncToDate, perFileDays),
    csvImport,
    accountCodeInferredFromReport,
    statementTimeZoneInferredFromReport,
    requiresTradeTimestampTimezone,
    portalSetup,
    csvImportEndpoint: csvEndpoint,
    syncEndpoint:
      openapiSyncEndpoint ||
      (raw.sync_endpoint != null ? String(raw.sync_endpoint) : undefined),
    syncLookbackDays: lookbackDays,
    syncFromDate,
    syncToDate,
    historyBeforeSyncRequiresCsvImport,
    csvMaxLookbackDaysPerFile,
    csvImportNotes,
    tradeSyncNotes,
    fullHistoryRecommendation,
  };
}

export function brokerTradeSyncCredentialLabel(
  tradeSync: BrokerTradeSyncInfo | null | undefined,
  field: string,
  fallback: string
): string {
  const fromSync = tradeSync?.credentialFieldLabels?.[field];
  const label = fromSync && fromSync.trim() ? fromSync.trim() : fallback;
  return label;
}

export function brokerCsvImportFieldLabel(
  csvImport: BrokerCsvImportCaps | null | undefined,
  field: string,
  fallback: string,
  options?: { stripExample?: boolean }
): string {
  const fromApi = csvImport?.fieldLabels?.[field];
  const label = fromApi && fromApi.trim() ? fromApi.trim() : fallback;
  if (options?.stripExample) {
    return stripExampleFromFieldLabel(label);
  }
  return label;
}

/** Card / tab description from capabilities (no hardcoded IBKR copy). */
export function brokerTradeSyncShortDescription(
  tradeSync: BrokerTradeSyncInfo | null | undefined,
  options?: { emphasizeCsv?: boolean }
): string {
  if (!tradeSync) return "";
  if (tradeSync.summary && !isVerboseIbkrApiSummary(tradeSync.summary)) {
    if (options?.emphasizeCsv && tradeSync.fullHistoryRecommendation === "csv_import") {
      return `${tradeSync.summary} Use the CSV import tab for older statement history.`;
    }
    return tradeSync.summary;
  }
  const perFile = tradeSync.csvMaxLookbackDaysPerFile ?? tradeSync.syncLookbackDays;
  if (options?.emphasizeCsv) {
    return `Import Flex statement CSVs (up to ${perFile} days each), then use Broker Sync for recent trades.`;
  }
  return `Connect Flex credentials and sync the last ${tradeSync.syncLookbackDays} days. Use CSV import for older history.`;
}

/** Shown on Broker Sync when user chose the CSV path for older history. */
export function ibkrOlderHistoryChoiceMessage(tradeSync: BrokerTradeSyncInfo): string {
  const perFile = tradeSync.csvMaxLookbackDaysPerFile ?? tradeSync.syncLookbackDays;
  const days = tradeSync.syncLookbackDays;
  return `You selected that you need history older than the last ${days} days. Import one Flex statement CSV per ${perFile}-day window (e.g. two files for two years), then use Broker Sync for recent trades.`;
}
